import { Testdatenblatt, TestdatenblattUpdateRequest } from '../../../models/testdatenblatt.model';

/**
 * Spreadsheet editor state: {@link cols} holds the column names, {@link rows}
 * holds the cell values as parallel string arrays (one entry per column, in
 * column order), {@link active} the on/off switch of each row.
 */
export interface GridState {
  cols: string[];
  rows: string[][];
  active: boolean[];
}

/**
 * Hydrates the grid from a loaded sheet. Each stored row is a
 * column-name → value map; it is projected onto the column order, filling
 * missing cells with empty strings. An empty sheet yields one empty column and
 * one empty row so the editor always has something to render.
 */
export function toGridState(sheet: Testdatenblatt): GridState {
  const cols = sheet.spalten.length > 0 ? [...sheet.spalten] : ['variable_1'];
  const rows =
    sheet.zeilen.length > 0 ? sheet.zeilen.map((map) => cols.map((c) => map[c] ?? '')) : [cols.map(() => '')];
  const active = rows.map((_, i) => sheet.zeilenAktiv?.[i] ?? true);
  return { cols, rows, active };
}

/**
 * Makes column names unique by suffixing duplicates (`name`, `name_2`, …).
 * Rows are stored as column-name → value maps, so two equal column names would
 * collapse into a single key on save and silently drop cell values. Deduping
 * before the maps are built keeps every column — and thus every cell — distinct.
 */
export function dedupeColumnNames(cols: string[]): string[] {
  const seen = new Set<string>();
  return cols.map((name) => {
    let candidate = name;
    let n = 2;
    while (seen.has(candidate)) candidate = `${name}_${n++}`;
    seen.add(candidate);
    return candidate;
  });
}

/** Next free `variable_N` column name that does not collide with an existing one. */
export function nextColumnName(cols: string[]): string {
  const existing = new Set(cols);
  let n = cols.length + 1;
  while (existing.has(`variable_${n}`)) n++;
  return `variable_${n}`;
}

/**
 * Builds the create/update request from the editor state. Each row array is
 * turned back into a column-name → value map. Module links are left out: they
 * are edited on the module side, and sending them here would overwrite links
 * set there in the meantime. Version and notes are always sent, empty as "",
 * because the server leaves a missing field unchanged.
 */
export function toSaveRequest(
  name: string,
  beschreibung: string,
  assigneeUserId: string,
  cols: string[],
  rows: string[][],
  active: boolean[],
  tags: string[],
  texts: { version: string; notes: string } = { version: '', notes: '' },
): TestdatenblattUpdateRequest {
  const spalten = dedupeColumnNames(cols);
  return {
    name: name.trim(),
    beschreibung: beschreibung.trim() || null,
    spalten,
    zeilen: rows.map((row) => Object.fromEntries(spalten.map((c, i) => [c, row[i] ?? '']))),
    zeilenAktiv: rows.map((_, i) => active[i] ?? true),
    assigneeUserId: assigneeUserId.trim() || null,
    tags,
    version: texts.version.trim(),
    notes: texts.notes,
  };
}
